import React from "react";
import FocusLock from "react-focus-lock";
import useMeasure from "react-use-measure";

import { faXmark } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { DialogContent, DialogOverlay } from "@reach/dialog";
import { animated, config as springConfigs, useSpring } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import cx from "classnames";

import { useMedia } from "~/lib/hooks";
import IconButton from "~/ui/button/IconButton";

import styles from "./css/Modal.module.css";

type DivProps = React.HTMLProps<HTMLDivElement>;
type HeadingProps = React.HTMLProps<HTMLHeadingElement>;
type ElementProps = React.HTMLProps<HTMLElement>;

/** Returns a promise that resolves when the closing animation has completed. */
const ModalCloseAnimationContext = React.createContext<(() => Promise<void>) | null>(null);

export function useModalCloseAnimation(): (() => Promise<void>) | null {
	return React.useContext(ModalCloseAnimationContext);
}

/** A button that animates the modal closed when clicked, _then_ calls the onClick handler. */
export function CloseButton({
	onClick,
	className,
	...rest
}: React.PropsWithChildren<React.ButtonHTMLAttributes<HTMLButtonElement>>) {
	const animateClosed = useModalCloseAnimation();
	const animateThenClick = React.useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			await animateClosed?.();
			onClick?.(event);
		},
		[onClick, animateClosed],
	);

	return (
		<IconButton
			variant="tertiary"
			title="Close"
			icon={faXmark}
			className={cx(styles.iconButton, className)}
			onClick={animateThenClick}
			data-test-id="modal-header-close-button"
			{...rest}
		/>
	);
}

type HeaderActionsProps = React.PropsWithChildren<DivProps> & {
	onClose?: React.MouseEventHandler<HTMLButtonElement>;
	closeButtonClassName?: string;
};

/**
 * To be used if the Modal Header requires any additional actions (eg. Information).
 * If this static is used remember to remove onClose from the <Modal.Header>.
 */
export function HeaderActions({
	children,
	onClose,
	className,
	closeButtonClassName,
	...rest
}: HeaderActionsProps): React.ReactElement {
	return (
		<div className={cx(styles.headerActions, className)} {...rest}>
			{children}
			{onClose && <CloseButton onClick={onClose} className={closeButtonClassName} />}
		</div>
	);
}
HeaderActions.displayName = "Modal.HeaderActions";

type TitleProps = React.PropsWithChildren<HeadingProps>;

export function Title({ children, className, ...rest }: TitleProps): React.ReactElement {
	return (
		<h1 className={cx(styles.headerTitle, className)} {...rest}>
			{children}
		</h1>
	);
}
Title.displayName = "Modal.Title";

export type HeaderProps = {
	onClose?: React.MouseEventHandler<HTMLButtonElement>;
	noBottomBorder?: boolean;
};

export function Header({
	children,
	className,
	noBottomBorder,
	onClose,
	...rest
}: React.PropsWithChildren<ElementProps> & HeaderProps) {
	return (
		<header className={cx(styles.header, { [styles.headerBorder]: !noBottomBorder }, className)} {...rest}>
			{children}
			{onClose && (
				<div className={styles.headerActions}>
					<CloseButton onClick={onClose} />
				</div>
			)}
		</header>
	);
}
Header.displayName = "Modal.Header";

export type BodyProps = {
	dark?: boolean;
	noPadding?: boolean;
	/** Only reduces upper padding */
	textOnly?: boolean;
};

export const Body = React.forwardRef<HTMLElement, React.PropsWithChildren<ElementProps> & BodyProps>(
	({ children, className, dark, noPadding, textOnly, ...rest }, ref) => {
		return (
			<main
				className={cx(
					styles.main,
					{ [styles.mainNoPadding]: noPadding, [styles.mainTextOnly]: textOnly, [styles.darkMain]: dark },
					className,
				)}
				ref={ref}
				{...rest}
			>
				{children}
			</main>
		);
	},
);
Body.displayName = "Modal.Body";

type FooterSecondaryActions = React.PropsWithChildren<DivProps>;

export function FooterSecondaryActions({ children, className, ...rest }: FooterSecondaryActions): React.ReactElement {
	return (
		<div className={cx(styles.footerSecondaryActions, className)} {...rest}>
			{children}
		</div>
	);
}
FooterSecondaryActions.displayName = "Modal.FooterSecondaryActions";

type FooterProps = React.PropsWithChildren<ElementProps> & {
	noTopBorder?: boolean;
};

export function Footer({ children, className, noTopBorder, ...rest }: FooterProps) {
	return (
		<footer className={cx(styles.footer, className, { [styles.footerBorder]: !noTopBorder })} {...rest}>
			{children}
		</footer>
	);
}
Footer.displayName = "Modal.Footer";

function DragIndicator() {
	return <div className={styles.dragIndicator} />;
}

// eslint-disable-next-line no-underscore-dangle
export let __isClosingModal = false;

declare module "csstype" {
	interface Properties {
		"--modal-width"?: string;
		/** The default height of a modal */
		"--modal-height"?: string;
	}
}

export type ModalProps = Omit<React.ComponentPropsWithoutRef<typeof DialogOverlay>, "onDismiss"> & {
	onClose: () => void;
	/** Sets the width of a Modal on desktop devices*/
	width?: string;
	/** Sets the default height of a Modal.
	 *
	 * It will automatically be limited to stay within safe areas and below the
	 * AppTitleBar. */
	height?: string;
	// We use this to tell focus lock to ignore a node if it contains a class included in this array
	// We need to ignore nodes of inputs inside popups that are rendered inside a modal
	// Otherwise they don't behave properly. That being said there might be a better way to achieve this
	lockIgnoredClasses?: Array<string>;
	lockIgnoredAttrs?: Array<string>;
	overlayClassName?: string;
	/** Prevents dismissing the modal by clicking on the overlay */
	disableOutsideClick?: boolean;
	/**
	 * A React Node to include in front of the overlay but behind the modal.
	 * I used this to place the confetti in onboarding. -Wal
	 */
	outsideContent?: React.ReactNode;
	/**
	 * Whether to animate height changes on mobile devices
	 * @todo Maybe we shouldn't ever animate height when the `height` prop is set?
	 */
	animateHeight?: boolean;
};

const horizontalShaking = [
	{ transform: "translateX(0)" },
	{ transform: "translateX(5px)" },
	{ transform: "translateX(-5px)" },
	{ transform: "translateX(5px)" },
	{ transform: "translateX(-5px)" },
	{ transform: "translateX(5px)" },
	{ transform: "translateX(-5px)" },
	{ transform: "translateX(5px)" },
	{ transform: "translateX(0)" },
];

const shakeAnimationTiming = {
	duration: 500,
	iterations: 1,
};

const shakeModalContent =
	(onClickCallback?: React.MouseEventHandler<HTMLDivElement>) =>
	(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		const modalContent = (event.target as Element).querySelector(`[data-reach-dialog-content]`);

		modalContent?.animate(horizontalShaking, shakeAnimationTiming);

		return onClickCallback?.(event);
	};

const Modal = React.forwardRef<HTMLDivElement, ModalProps>(function Modal(
	{
		className,
		children,
		width = "31.25rem",
		height: heightProp,
		isOpen: isOpenProp,
		lockIgnoredClasses,
		lockIgnoredAttrs,
		overlayClassName,
		disableOutsideClick,
		outsideContent,
		onClick,
		initialFocusRef,
		onClose,
		animateHeight = true,
		...rest
	}: ModalProps,
	ref,
) {
	if (process.env.NODE_ENV !== "production") {
		if (heightProp && /\d(vh|%)/.test(heightProp)) {
			console.error(
				"Modal `height`: `vh` units will be larger than expected on iOS Safari, and % will not work; " +
					"for Modals' `height` prop, use dynamic viewport-aware units, e.g. `dvh`. \n" +
					'For example, use `height="100dvh"` to make the modal as tall as possible. ' +
					"It will automatically stay within safe areas, and below the AppTitleBar. \n" +
					// eslint-disable-next-line max-len -- can't make the URL any shorter
					"See: https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Values/length#relative_length_units_based_on_viewport",
			);
		}
	}
	const height = heightProp?.replace(/(\d)(vh|%)/, "$1dvh");
	/**
	 * Since moving the initial focus is handled by the focus lock, and we disable the automatic
	 * Focus Lock provided by Reach, we have to provide this ourselves. This is mimicking their
	 * implementation as directly as possible.
	 * @see https://github.com/reach/reach-ui/blob/d0fd3fb611b37c7e22efb0681f68b52ce651e28c/packages/dialog/src/index.tsx#L163
	 */
	const activateFocusLock = React.useCallback(() => {
		if (initialFocusRef && initialFocusRef.current) {
			initialFocusRef.current.focus();
		}
	}, [initialFocusRef]);

	/** Whether to use "sheet" visual appearance, i.e. bottom aligned, sliding animation */
	const mobileSheetStyle = useMedia("(max-width: 734px)");
	const isTouchscreen = useMedia("(pointer: coarse) and (hover: none)");

	// Cleanup on unmount (equivalent to componentWillUnmount)
	React.useEffect(() => {
		return () => {
			// enables tooltips to not be displayed on input re-focus after a modal is closed
			__isClosingModal = true;
			setTimeout(() => {
				__isClosingModal = false;
			}, 0);
		};
	}, []);

	const isWhitelisted = React.useCallback(
		(node: HTMLElement) => {
			// This allows for an element to have the attribute "data-ignore-focus-lock" and have it and all its children ignored
			if (node.closest("[data-ignore-focus-lock]") != null) return false;

			// This method and its uses could likely be refactored to use the method above
			let containsIgnoredClass = false;
			let containsIgnoredAttr = false;
			if (lockIgnoredClasses) {
				containsIgnoredClass = lockIgnoredClasses.some(lockIgnoredClass => node.className.includes(lockIgnoredClass));
			}
			if (lockIgnoredAttrs) {
				containsIgnoredAttr = lockIgnoredAttrs.some(lockIgnoredAttr => node.hasAttribute(lockIgnoredAttr));
			}
			return node.nodeName !== "IFRAME" && !containsIgnoredClass && !containsIgnoredAttr;
		},
		[lockIgnoredClasses, lockIgnoredAttrs],
	);

	// 0 is fully hidden, 1 is fully open
	const [{ springPosition }, springApi] = useSpring(() => ({
		from: { springPosition: 0 },
		config: {
			// Slightly more tension and friction than `stiff`, for very little overshoot
			tension: 250,
			friction: 25,
		},
	}));
	/** Reach defaults to open if `isOpen` is not provided, but
	 * we still want to provide it the prop exactly as we got it,
	 * so this is a separate variable from the prop. */
	const isOpen = isOpenProp ?? true;
	React.useEffect(() => {
		springApi.start({ springPosition: isOpen ? 1 : 0 });
	}, [springApi, isOpen]);
	const animateClosed = React.useCallback(
		() =>
			new Promise<void>(resolve => {
				springApi.start({
					springPosition: 0,
					onRest: result => {
						if (result.value.springPosition === 0) resolve();
					},
				});
			}),
		[springApi],
	);
	const handleClose = React.useCallback(async () => {
		await animateClosed();
		onClose();
	}, [animateClosed, onClose]);
	const handleOpen = React.useCallback(() => {
		springApi.start({
			springPosition: 1,
		});
	}, [springApi]);

	const [contentMeasureRef, { height: measuredContentHeight }] = useMeasure();
	const overlayRef = React.useRef<HTMLDivElement>(null);

	const [{ animatedHeight }] = useSpring(
		{
			animatedHeight: measuredContentHeight || "auto",
			config: {
				...springConfigs.stiff,
				clamp: true,
			},
		},
		[measuredContentHeight],
	);

	const bindDrag = useDrag(
		({ last, velocity: [, vy], direction: [, dy], offset: [, oy], cancel, event, elapsedTime }) => {
			// We only want this gesture to happen on touch screens, not with mouse dragging.
			// But we also don't want to set the `pointer.touch` option to true, because that
			// will make it use touch events instead of pointer events, which don't get cancelled
			// if they're detected as a scroll, which we _do_ want. So we have to filter for just
			// pointer events that are for a touch manually.
			// See: https://use-gesture.netlify.app/docs/options/#pointertouch
			if (!(event instanceof PointerEvent) || event.pointerType !== "touch") return;

			if (disableOutsideClick) return;

			// Ignore events that originated from a nested modal so we only drag one at a time
			const target = event.target as HTMLElement;
			if (target.closest("[data-reach-dialog-overlay]") !== overlayRef.current) return;

			// iOS waits 50-70ms before sending `pointercancel` if the touch is scrolling,
			// so wait until then before we assume it _isn't_ scrolling. Otherwise we get
			// a couple of frames where we move the sheet before resetting.
			if (elapsedTime < 80) return;

			if (event.type === "pointercancel") {
				// For some reason `pointercancel` is bound to the `pointerup`
				// handler, so if we start scrolling, that calls with `last`
				// as though the gesture had ended. Instead we immediately reset
				// to original position
				springApi.set({ springPosition: isOpen ? 1 : 0 });
				cancel();
				return;
			}

			// `last` means the user released the gesture, so close the modal if it's
			// been dragged far enough or swiped fast enough, or reset to open position
			if (last) {
				const height = measuredContentHeight || window.innerHeight;
				oy > height * 0.5 || (vy > 0.5 && dy > 0) ? handleClose() : handleOpen();
			}
			// The user is still dragging, so move the sheet around
			else {
				const height = measuredContentHeight || window.innerHeight;
				springApi.set({ springPosition: 1 - oy / height });
			}
		},
		{
			from: () => [0, (1 - springPosition.get()) * measuredContentHeight],
			filterTaps: true,
			threshold: 15,
			pointer: {
				keys: false,
				capture: false,
			},
			bounds: { top: 0 },
			rubberband: true,
		},
	);

	return (
		<DialogOverlay
			dangerouslyBypassFocusLock // without this, users can't type on Intercom
			ref={overlayRef}
			isOpen={isOpenProp}
			onClick={disableOutsideClick ? shakeModalContent(onClick) : onClick}
			onDismiss={disableOutsideClick ? undefined : handleClose}
			className={cx(styles.overlay, overlayClassName)}
			{...rest}
			as={animated.div}
			style={{
				backgroundColor: springPosition
					.to([0, 1], [0, 0.6])
					.to(value => `rgb(from var(--dimmerColor) r g b / ${value ?? 0})`),
			}}
			{...bindDrag()}
		>
			{outsideContent}
			<FocusLock
				autoFocus
				returnFocus
				onActivation={activateFocusLock}
				whiteList={isWhitelisted}
				className={styles.focusLock}
			>
				<DialogContent
					as={animated.div}
					aria-label="dialog-label"
					ref={ref}
					className={cx(styles.modal, className)}
					style={{
						"--modal-width": width,
						"--modal-height": height,
						// Only animate height on mobile and touchscreens – feels weird on desktop
						height: mobileSheetStyle && isTouchscreen && animateHeight ? animatedHeight : undefined,
						...(mobileSheetStyle
							? {
									y: springPosition.to([0, 1], ["100%", "0"]),
								}
							: {
									opacity: springPosition,
									y: springPosition.to([0, 1], [5, 0]),
									scale: springPosition.to([0, 1], [0.99, 1]),
								}),
						willChange: "transform",
					}}
				>
					{mobileSheetStyle && !disableOutsideClick && isTouchscreen && <DragIndicator />}
					<ModalCloseAnimationContext.Provider value={animateClosed}>
						<div ref={contentMeasureRef} className={styles.contentWrapper}>
							{children}
						</div>
					</ModalCloseAnimationContext.Provider>
				</DialogContent>
				{mobileSheetStyle && (
					<animated.div
						className={styles.rubberBandExtension}
						style={{
							height: springPosition.to([1, 2], [0, measuredContentHeight]),
						}}
					/>
				)}
			</FocusLock>
		</DialogOverlay>
	);
});

type ModalWithStatics = typeof Modal & {
	Header: typeof Header;
	Title: typeof Title;
	HeaderActions: typeof HeaderActions;
	Body: typeof Body;
	Footer: typeof Footer;
	FooterSecondaryActions: typeof FooterSecondaryActions;
};

const ModalWithStatics = Modal as ModalWithStatics;
ModalWithStatics.Header = Header;
ModalWithStatics.Title = Title;
ModalWithStatics.HeaderActions = HeaderActions;
ModalWithStatics.Body = Body;
ModalWithStatics.Footer = Footer;
ModalWithStatics.FooterSecondaryActions = FooterSecondaryActions;

export default ModalWithStatics;
